import Image from 'next/image';
import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import { Dispatch, SetStateAction } from 'react';

import greaterCareImage from '@/../public/greaterCare/plans-and-pricing/getting-started.webp';
import greaterCareImageMW from '@/../public/greaterCare/plans-and-pricing/getting-started-mw.webp';
import useResponsive from '@/components/hooks/useResponsive';
import { AnalyticsEventName, CtaLocation, CtaText, ScreenName } from '@/types/enums';
import { AnalyticsHelper } from '@/utilities/AnalyticsHelper';

// Steps Data
export const steps = [
  {
    number: 1,
    title: 'Share your care needs',
    description: 'From pay to schedule to specific needs—share what you’re looking for.',
  },
  {
    number: 2,
    title: 'Browse caregivers',
    description: 'Check out profiles, compare rates, and read reviews from families like yours.',
  },
  {
    number: 3,
    title: 'Pick your plan',
    description: 'Choose the Premium Plan to start messaging the caregivers you’re interested in.',
  },
];

interface GettingStartedProps {
  setOpenModalOrDrawer: Dispatch<SetStateAction<boolean>>;
}

const GettingStarted = ({ setOpenModalOrDrawer }: GettingStartedProps) => {
  const theme = useTheme();
  const { isDesktopLG, isMobile } = useResponsive();

  const handleGetStartedClick = () => {
    setOpenModalOrDrawer(true);
    AnalyticsHelper.logEvent({
      name: AnalyticsEventName.CTA_INTERACTED,
      data: {
        screen_name: ScreenName.PLANS_AND_PRICING,
        enrollment_initiated: true,
        cta_text: CtaText.GET_STARTED,
        cta_location: CtaLocation.HOW_IT_WORKS,
        vertical: 'Unknown',
      },
    });
  };

  return (
    <Box
      id="getting-started"
      data-testid="getting-started-section"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        pt: { xs: 3, lg: 16 },
        pb: { xs: 8, lg: 16 },
        backgroundColor: '#FAF7F7',
      }}>
      {!isDesktopLG && (
        <Typography
          variant="display3"
          textAlign="center"
          px={3}
          sx={{
            mt: 5,
            mb: 4,
          }}>
          Getting started is simple
        </Typography>
      )}

      <Stack
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'center', md: 'center' },
          gap: { xs: 6, md: 6, lg: 16 },
          justifyContent: 'center',
          px: 3,
          width: '100%',
          '@media (min-width: 1728px)': {
            justifyContent: 'left',
            alignItems: 'left',
            pl: 10,
          },
          maxWidth: 1900,
        }}>
        <Box
          sx={{
            width: { xs: 375, sm: 330, lg: 592 },
            height: { xs: 264, sm: 354, lg: 668 },
            borderRadius: '24px',
            position: 'relative',
            overflow: 'hidden',
            flexShrink: 0,
            '& .getting-started-image': {
              objectFit: 'cover',
              objectPosition: 'top',
            },
          }}>
          <Image
            src={isMobile ? greaterCareImageMW : greaterCareImage}
            alt="Mother sharing phone with daughter"
            className="getting-started-image"
            layout="fill"
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: { xs: 'center', md: 'flex-start' },
            width: { xs: '100%', sm: 500, lg: 600 },
            px: { xs: 3 },
          }}>
          {isDesktopLG && (
            <Typography
              variant="display2"
              sx={{
                mt: 0,
                mb: 6,
                maxWidth: '100%',
              }}>
              Getting started is simple
            </Typography>
          )}

          {steps.map(({ number, title, description }) => (
            <Box
              key={number}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: { xs: 3, lg: 4 },
                mb: { xs: 3, lg: 4.5 },
              }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  minWidth: 48,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.brand.accent,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontWeight: 'bold',
                }}>
                <Typography
                  sx={{
                    variant: 'h2',
                    color: theme.palette.brand.main,
                    fontWeight: 'bold',
                  }}>
                  {number}
                </Typography>
              </Box>
              <Box>
                <Typography variant={isDesktopLG ? 'h2' : 'h3'} mb={1}>
                  {title}
                </Typography>
                <Typography
                  variant={isDesktopLG ? 'body2' : 'body3'}
                  color={theme.palette.primary.main}
                  sx={{
                    lineHeight: '20px',
                  }}>
                  {description}
                </Typography>
              </Box>
            </Box>
          ))}

          <Button
            onClick={handleGetStartedClick}
            size="large"
            sx={{
              borderRadius: '32px',
              mt: { xs: 1, lg: 2 },
              width: { xs: '100%', sm: 171 },
            }}
            color="primary"
            variant="contained">
            {CtaText.GET_STARTED}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default GettingStarted;
